/**
 * Shared Google Maps Script Loader
 *
 * This module provides a singleton pattern for loading the Google Maps JavaScript API.
 * It ensures the script is only loaded once, even if multiple components request it.
 */

import { getStoredThemeMode, resolveDark } from './theme';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// AdvancedMarkerElement (the replacement for the deprecated google.maps.Marker)
// only renders on a Map created with a `mapId`. Provide one via env for production;
// fall back to Google's public DEMO_MAP_ID (shows a faint "for development only"
// watermark) so markers still render before a real Map ID is provisioned.
// Create a real Map ID at: Google Cloud Console → Google Maps Platform → Map Management.
const GOOGLE_MAPS_MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

// Singleton state
let loadPromise: Promise<void> | null = null;
let isLoaded = false;

// Google calls window.gm_authFailure() when the key/referer/billing is rejected
// (the #1 reason a map silently fails to render). Capture it so components can
// show an actionable message instead of a blank map. The current origin is what
// must be whitelisted in the Google Cloud console → API key → "Website restrictions".
let authFailed = false;
if (typeof window !== 'undefined') {
  (window as unknown as { gm_authFailure?: () => void }).gm_authFailure = () => {
    authFailed = true;
    // eslint-disable-next-line no-console
    console.error(
      `[GoogleMaps] Authentication failed. The API key likely does not allow this origin: ` +
      `${window.location.origin}. Add it under Google Cloud Console → Credentials → your key → ` +
      `Application restrictions → Website restrictions (e.g. ${window.location.origin}/*), and ensure ` +
      `the "Maps JavaScript API" is enabled with billing active.`,
    );
  };
}

/** True if Google reported an auth/referer/billing failure for the Maps key. */
export function didGoogleMapsAuthFail(): boolean {
  return authFailed;
}

/**
 * Check if Google Maps API is already loaded AND the core `Map` constructor is
 * actually available. With `loading=async`, the `google.maps` namespace can
 * exist (e.g. the `places` library resolved) while `google.maps.Map` is not yet
 * a constructor — checking the namespace alone causes "google.maps.Map is not a
 * constructor" crashes in consumers. So we check the constructor itself.
 */
export function isGoogleMapsLoaded(): boolean {
  return (
    isLoaded &&
    typeof window !== 'undefined' &&
    typeof window.google?.maps?.Map === 'function'
  );
}

/**
 * Resolve once the core map classes are actually usable. In `loading=async`
 * mode this means importing the `maps` (and `places`) libraries; the bootstrap
 * exposes `google.maps.importLibrary`. Falls back to a short poll if needed.
 */
async function ensureCoreReady(): Promise<void> {
  // "Ready" requires the core Map constructor, the marker library
  // (AdvancedMarkerElement) AND the places library. With loading=async the
  // libraries resolve independently — google.maps.Map can be a function while
  // google.maps.marker or google.maps.places is still undefined — so checking
  // Map alone lets consumers crash with "Cannot read properties of undefined
  // (reading 'AdvancedMarkerElement' / 'PlacesService' / 'Autocomplete')".
  // PlacesService present ⇒ the whole places library (incl. Autocomplete) is
  // loaded, so this also covers the location-autocomplete consumers.
  const ready = () =>
    typeof window.google?.maps?.Map === 'function' &&
    typeof window.google?.maps?.marker?.AdvancedMarkerElement === 'function' &&
    typeof window.google?.maps?.places?.PlacesService === 'function';

  if (ready()) return;

  const importLib = window.google?.maps?.importLibrary as
    | ((name: string) => Promise<unknown>)
    | undefined;
  if (importLib) {
    // Explicitly await the marker library too (not just maps/places), otherwise
    // AdvancedMarkerElement may be undefined when the map renders.
    await Promise.all([importLib('maps'), importLib('places'), importLib('marker')]);
    if (ready()) return;
  }

  // Fallback: poll up to ~5s for the Map constructor + marker + places libraries.
  await new Promise<void>((resolve, reject) => {
    const start = Date.now();
    const timer = setInterval(() => {
      if (ready()) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - start > 5000) {
        clearInterval(timer);
        reject(new Error('Google Maps core/marker/places libraries failed to initialize'));
      }
    }, 50);
  });
}

/**
 * Load the Google Maps JavaScript API
 * Returns a promise that resolves when the API is ready to use.
 * Safe to call multiple times - will only load the script once.
 */
export function loadGoogleMaps(): Promise<void> {
  // Already loaded
  if (isGoogleMapsLoaded()) {
    return Promise.resolve();
  }

  // Already loading - return existing promise
  if (loadPromise) {
    return loadPromise;
  }

  // Check if script tag already exists (e.g., from a previous session or external source)
  const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
  if (existingScript) {
    // Script exists but may not be loaded yet
    if (window.google?.maps) {
      // google.maps may exist while the marker library isn't ready yet — ensure
      // both the core and marker libraries before resolving (don't short-circuit).
      loadPromise = ensureCoreReady()
        .then(() => { isLoaded = true; })
        .catch((err) => { loadPromise = null; throw err; });
      return loadPromise;
    }

    // Wait for existing script to load
    loadPromise = new Promise((resolve, reject) => {
      const finish = () => {
        ensureCoreReady()
          .then(() => { isLoaded = true; resolve(); })
          .catch((err) => { loadPromise = null; reject(err); });
      };
      existingScript.addEventListener('load', finish);
      existingScript.addEventListener('error', () => {
        loadPromise = null;
        reject(new Error('Failed to load Google Maps script'));
      });

      // In case the script already finished loading before we attached listeners.
      setTimeout(() => {
        if (window.google?.maps) finish();
      }, 100);
    });

    return loadPromise;
  }

  // Create and load new script
  loadPromise = new Promise((resolve, reject) => {
    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('Google Maps API key not configured'));
      return;
    }

    const script = document.createElement('script');
    // libraries=places,drawing,geometry,marker — drawing powers the polygon
    // "draw your area" tool on the search map; geometry powers
    // poly.containsLocation() for filtering properties by polygon; marker
    // provides AdvancedMarkerElement (replaces the deprecated google.maps.Marker).
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,drawing,geometry,marker&language=en&loading=async`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';

    script.onload = () => {
      ensureCoreReady()
        .then(() => { isLoaded = true; resolve(); })
        .catch((err) => { loadPromise = null; reject(err); });
    };

    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Get the API key (for components that need it)
 */
export function getGoogleMapsApiKey(): string {
  return GOOGLE_MAPS_API_KEY;
}

/**
 * Get the Map ID required by AdvancedMarkerElement. Returns the configured
 * `VITE_GOOGLE_MAPS_MAP_ID`, or Google's DEMO_MAP_ID fallback when unset.
 * Pass this as `mapId` to every `new google.maps.Map(...)` that renders markers.
 */
export function getGoogleMapsMapId(): string {
  return GOOGLE_MAPS_MAP_ID;
}

/**
 * The Google Maps `colorScheme` (DARK/LIGHT) to match the app theme. Pass this
 * as a `colorScheme` map option so vector maps (which we always create with a
 * Map ID) render dark in dark mode. Note: colorScheme is read at construction
 * only — a theme change after a map is built needs a remount to take effect.
 * Call this at map-init time (the Maps API must be loaded for the enum).
 */
export function getMapColorScheme(): google.maps.ColorScheme {
  return resolveDark(getStoredThemeMode())
    ? google.maps.ColorScheme.DARK
    : google.maps.ColorScheme.LIGHT;
}
